import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const RegisterForm = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    full_name: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  
  const { register, login } = useAuth();
  const navigate = useNavigate();
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field-specific errors when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Username validation
    if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Password validation
    if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    // Password confirmation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setGeneralError('');
    
    try {
      // Remove confirmPassword field before sending to API
      const { confirmPassword, ...registerData } = formData;
      
      // Register the user
      await register(registerData);
      
      // Automatically log in after successful registration
      await login(formData.username, formData.password);
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Registration error:', err);
      
      // Handle API validation errors
      if (err.response?.data?.detail) {
        setGeneralError(err.response.data.detail);
      } else if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setGeneralError('Registration failed. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="flex flex-col items-center justify-center w-full max-w-md p-6 mx-auto bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md transition-colors">
      <div className="w-full">
        <h2 className="mb-6 text-3xl px-2 sm:px-20 font-bold text-center text-ui-light-text-primary dark:text-ui-dark-text-primary">
          Create an Account
        </h2>
        
        {generalError && (
          <div className="p-3 mb-4 text-sm text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-lg">
            {generalError}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block mb-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary" htmlFor="username">
              Username <span className="text-status-error-light-text dark:text-status-error-dark-text">*</span>
            </label>
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
              required
              value={formData.username}
              onChange={handleChange}
              className={`w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-background dark:bg-ui-dark-hover border ${
                errors.username ? 'border-status-error-light-text dark:border-status-error-dark-text' : 'border-ui-light-border dark:border-ui-dark-border'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400`}
              placeholder="Choose a username"
            />
            {errors.username && (
              <p className="mt-1 text-xs text-status-error-light-text dark:text-status-error-dark-text">{errors.username}</p>
            )}
          </div>
          
          <div>
            <label className="block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300" htmlFor="email">
              Email <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-2 text-neutral-700 dark:text-neutral-200 bg-white dark:bg-neutral-700 border ${
                errors.email ? 'border-red-500 dark:border-red-400' : 'border-neutral-300 dark:border-neutral-600'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400`}
              placeholder="Your email address"
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.email}</p>
            )}
          </div>
          
          <div>
            <label className="block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300" htmlFor="full_name">
              Full Name
            </label>
            <input
              id="full_name"
              name="full_name"
              type="text"
              autoComplete="name"
              value={formData.full_name}
              onChange={handleChange}
              className="w-full px-3 py-2 text-neutral-700 dark:text-neutral-200 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
              placeholder="Your full name (optional)"
            />
          </div>
          
          <div>
            <label className="block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300" htmlFor="password">
              Password <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              value={formData.password}
              onChange={handleChange}
              className={`w-full px-3 py-2 text-neutral-700 dark:text-neutral-200 bg-white dark:bg-neutral-700 border ${
                errors.password ? 'border-red-500 dark:border-red-400' : 'border-neutral-300 dark:border-neutral-600'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400`}
              placeholder="Create a password"
            />
            {errors.password && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.password}</p>
            )}
          </div>
          
          <div>
            <label className="block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300" htmlFor="confirmPassword">
              Confirm Password <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`w-full px-3 py-2 text-neutral-700 dark:text-neutral-200 bg-white dark:bg-neutral-700 border ${
                errors.confirmPassword ? 'border-red-500 dark:border-red-400' : 'border-neutral-300 dark:border-neutral-600'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400`}
              placeholder="Confirm your password"
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.confirmPassword}</p>
            )}
          </div>
          
          <div className="pt-2">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-4 py-2 text-sm font-medium text-white dark:text-neutral-100 bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-neutral-800 disabled:opacity-50 transition-colors"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-3 -ml-1 text-white dark:text-neutral-100 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Account...
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          </div>
        </form>
        
        <div className="mt-4 text-center">
          <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;